import React, { useState } from 'react';
import { TransportationInfo } from './types';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Plus, Car } from 'lucide-react';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { forceUpdateTransportationCoordinates } from "@/lib/quotation-storage";
import { TransportationForm } from './TransportationForm';
import { format } from 'date-fns';
import { PageSelectionModal } from './PageSelectionModal';
import { globalMoveTransportationToPage as globalMoveTransportationToPageFunc } from '@/hooks/useQuotationForm';

interface TransportationManagerProps {
  pageId: string;
  transportations: TransportationInfo[];
  updateTransportations: (transportations: TransportationInfo[]) => void;
  collapsedItems: Record<string, boolean>;
  onToggleItem: (id: string) => void;
  hideAddButton?: boolean;
  hideTitle?: boolean;
  allPages?: any[];
  removeTransportationFromPage?: (pageId: string, transportationId: string) => void;
  addTransportationToPage?: (pageId: string, transportation: TransportationInfo) => void;
  moveTransportationToPage?: (sourcePageId: string, transportationId: string, targetPageId: string) => void;
}

export function TransportationManager({ 
  pageId, 
  transportations, 
  updateTransportations,
  collapsedItems,
  onToggleItem,
  hideAddButton = false,
  hideTitle = false,
  allPages = [],
  removeTransportationFromPage,
  addTransportationToPage,
  moveTransportationToPage
}: TransportationManagerProps) {
  // Adicionar log para debug
  console.log('TransportationManager - allPages:', allPages);
  
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isProcessingTransportationDescription, setIsProcessingTransportationDescription] = useState(false);
  const [transportationToMove, setTransportationToMove] = useState<string | null>(null);
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);

  // Escutar eventos de expansão de itens para colapsar os traslados quando outro item for expandido
  React.useEffect(() => {
    const handleItemExpanded = (event: CustomEvent) => {
      const { itemType, itemId, pageId: eventPageId } = event.detail;
      
      // Se o evento for de outra página ou for para um traslado, ignorar
      if (eventPageId !== pageId || itemType === 'transportation') {
        return;
      }
      
      // Just notify parent component instead of trying to auto-collapse specific items
      onToggleItem('');
    };
    
    // Adicionar o event listener
    document.addEventListener('item-expanded', handleItemExpanded as EventListener);
    
    // Remover ao desmontar o componente
    return () => {
      document.removeEventListener('item-expanded', handleItemExpanded as EventListener);
    };
  }, [pageId, onToggleItem]);

  // Função para adicionar novo traslado
  const addTransportation = () => {
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const newTransportation: TransportationInfo = {
      id: `transportation-${Date.now()}`,
      name: '',
      date: formattedDate,
      time: '',
      duration: '',
      location: '',
      description: '',
      price: undefined,
      currency: 'BRL',
      images: [],
      globalOrder: transportations.length + 1  // Increment by 1 to place at the end
    };
    
    updateTransportations([...(transportations || []), newTransportation]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(newTransportation.id);
    }, 0);
  };

  // Função para remover um traslado
  const removeTransportation = (transportationId: string) => {
    const updatedTransportations = transportations.filter(transportation => transportation.id !== transportationId);
    updateTransportations(updatedTransportations);
  };

  // Função para atualizar um campo específico de um traslado
  const updateTransportation = (transportationId: string, field: keyof TransportationInfo, value: any) => {
    let safeValue;
    
    // Special handling for coordinates to ensure they're always properly formatted numbers
    if (field === 'coordinates' && value) {
      // Ensure lat and lng are valid numbers
      const lat = typeof value.lat === 'string' ? parseFloat(value.lat) : Number(value.lat);
      const lng = typeof value.lng === 'string' ? parseFloat(value.lng) : Number(value.lng);
      
      safeValue = {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng
      };
      
      console.log("[TransportationManager] Normalized coordinates:", {
        original: value,
        normalized: safeValue,
        types: {
          originalLatType: typeof value.lat,
          originalLngType: typeof value.lng,
          normalizedLatType: typeof safeValue.lat,
          normalizedLngType: typeof safeValue.lng
        }
      });
    } 
    // Special handling for address to ensure it's a valid string
    else if (field === 'address') {
      // Ensure address is a string and not undefined
      safeValue = value?.toString() || '';
      console.log("[TransportationManager] Processing address:", {
        originalValue: value,
        originalType: typeof value,
        safeValue,
        safeValueType: typeof safeValue,
        isEmpty: safeValue === '',
        transportationId
      });
    }
    else {
      // For all other fields, use regular deep copy
      safeValue = typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value;
    }
    
    const updatedTransportations = transportations.map((transportation: TransportationInfo) => {
      if (transportation.id === transportationId) {
        const updated = { ...transportation, [field]: safeValue };
        
        // Extra debugging for coordinate updates
        if (field === 'coordinates') {
          console.log('[TransportationManager] Updated transportation with new coordinates:', {
            transportation: transportation.id,
            name: transportation.name,
            oldCoordinates: transportation.coordinates,
            newCoordinates: safeValue,
            fullObject: updated
          });
          
          // Ensure coordinates are properly saved
          setTimeout(() => {
            forceUpdateTransportationCoordinates(transportationId, safeValue);
          }, 300);
        }
        
        // Extra debugging for address updates
        if (field === 'address') {
          console.log('[TransportationManager] Updated transportation with new address:', {
            transportation: transportation.id,
            name: transportation.name,
            oldAddress: transportation.address,
            newAddress: safeValue,
            addressType: typeof safeValue,
            fullObject: updated
          });
        }
        
        return updated;
      }
      return transportation;
    });
    
    console.log(`[TransportationManager] Updated field ${String(field)}:`, {
      transportationId,
      fieldName: field,
      newValue: safeValue,
      valueType: typeof safeValue,
      isObject: typeof safeValue === 'object',
      hasCoordinates: field === 'coordinates',
      isAddress: field === 'address'
    });
    
    updateTransportations(updatedTransportations);
  };

  // Função para atualizar múltiplos campos de um traslado de uma vez
  const batchUpdateTransportation = (transportationId: string, updates: Partial<TransportationInfo>) => {
    console.log('[TransportationManager] Batch updating transportation:', {
      transportationId,
      updates
    });
    
    const updatedTransportations = transportations.map((transportation: TransportationInfo) => {
      if (transportation.id === transportationId) {
        // Create a deep copy of the transportation to avoid reference issues
        const updated = JSON.parse(JSON.stringify({
          ...transportation,
          ...updates
        }));
        
        // Ensure coordinates are handled properly
        if (updates.coordinates) {
          updated.coordinates = {
            lat: Number(updates.coordinates.lat),
            lng: Number(updates.coordinates.lng)
          };
        }
        
        console.log('[TransportationManager] Updated transportation in batch:', {
          transportationId,
          name: transportation.name,
          updatedKeys: Object.keys(updates),
          address: {
            before: transportation.address,
            after: updated.address
          },
          coordinates: {
            before: transportation.coordinates,
            after: updated.coordinates
          }
        });
        
        return updated;
      }
      return transportation;
    });
    
    updateTransportations(updatedTransportations);
  };

  // Função para duplicar um traslado
  const duplicateTransportation = (pageId: string, transportationId: string) => {
    const transportationToDuplicate = transportations.find(
      (transportation) => transportation.id === transportationId
    );
    
    if (!transportationToDuplicate) return;
    
    // Deep clone the transportation object
    const duplicatedTransportation: TransportationInfo = JSON.parse(JSON.stringify(transportationToDuplicate));
    
    // Update properties for the duplicate
    duplicatedTransportation.id = `transportation-${Date.now()}`;
    // If the original has a name, append (Cópia), otherwise leave empty for the fallback pattern
    duplicatedTransportation.name = transportationToDuplicate.name ? `${transportationToDuplicate.name} (Cópia)` : '';
    duplicatedTransportation.globalOrder = transportations.length; // Place at the end
    
    // Add the duplicated transportation to the list
    const updatedTransportations = [...transportations, duplicatedTransportation];
    updateTransportations(updatedTransportations);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(duplicatedTransportation.id);
    }, 0);
    
    toast({
      title: "Traslado duplicado",
      description: "O traslado foi duplicado com sucesso.",
    });
  };

  // Função para alternar o estado de colapso de um traslado
  const toggleTransportationCollapse = (transportationId: string) => {
    // If empty ID, it's just a signal to collapse all - forward to parent component
    if (!transportationId) {
      onToggleItem('');
      return;
    }
    
    // Se o item atual está colapsado (fechado), vamos abri-lo e fechar todos os outros
    if (collapsedItems[transportationId]) {
      // Abrir apenas o item atual
      onToggleItem(transportationId);
      
      // Disparar um evento para notificar outros componentes que um traslado foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'transportation',
          itemId: transportationId,
          pageId: pageId
        }
      }));
    } else {
      // Se está aberto, apenas fechamos
      onToggleItem(transportationId);
    }
  };

  // Function to initiate the transportation move process
  const handleInitiateTransportationMove = (transportationId: string) => {
    console.log('[TransportationManager] handleInitiateTransportationMove called:', {
      transportationId,
      hasMoveFunction: typeof moveTransportationToPage === 'function',
      hasGlobalMoveFunction: typeof globalMoveTransportationToPageFunc === 'function',
      allPagesAvailable: Array.isArray(allPages),
      allPagesCount: allPages?.length,
      currentPageId: pageId
    });
    
    // Check if we have the necessary props and data
    if (!moveTransportationToPage && !globalMoveTransportationToPageFunc) {
      console.log('[TransportationManager] No movement function available (local or global)');
      toast({
        title: "Operação não disponível",
        description: "A função para mover traslados não está disponível.",
      });
      return;
    }
    
    // Guarantee that allPages is an array and properly filter
    const availablePages = Array.isArray(allPages) && allPages.length > 1
      ? allPages.filter(p => p.id !== pageId)
      : [];
    
    console.log('[TransportationManager] Available pages:', {
      count: availablePages.length,
      pageIds: availablePages.map(p => p.id),
      excludedPageId: pageId
    });
    
    if (availablePages.length === 0) {
      console.log('[TransportationManager] No available pages after filtering');
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover o traslado.",
      });
      return;
    }
    
    // Set the transportation to move and open the page selection modal
    setTransportationToMove(transportationId);
    setIsPageSelectionOpen(true);
  };

  // Handle page selection for transportation movement
  const handleMoveToPage = (targetPageId: string) => {
    console.log("[TransportationManager] handleMoveToPage called with targetPageId:", targetPageId);
    
    if (!transportationToMove) {
      console.log("[TransportationManager] No transportation to move, aborting");
      return;
    }
    
    // Find the transportation to move
    const transportation = transportations.find(t => t.id === transportationToMove);
    console.log("[TransportationManager] Transportation found:", transportation);
    
    if (!transportation) {
      console.log("[TransportationManager] Transportation not found in list, aborting");
      return;
    }
    
    // Close the modal
    setIsPageSelectionOpen(false);
    
    // Debug log for moveTransportationToPage and globalMoveTransportationToPageFunc availability
    console.log("[TransportationManager] Movement functions availability:", { 
      moveTransportationToPage: {
        exists: !!moveTransportationToPage,
        type: typeof moveTransportationToPage,
        isFunction: typeof moveTransportationToPage === 'function'
      },
      globalMoveTransportationToPageFunc: {
        exists: !!globalMoveTransportationToPageFunc,
        type: typeof globalMoveTransportationToPageFunc,
        isFunction: typeof globalMoveTransportationToPageFunc === 'function'
      },
      hasAllRequiredParams: pageId && transportationToMove && targetPageId
    });
    
    try {
      // Primary approach: use the prop function if available
      if (typeof moveTransportationToPage === 'function') {
        console.log("[TransportationManager] Calling prop function with:", {
          sourcePageId: pageId,
          transportationId: transportationToMove,
          targetPageId
        });
        
        moveTransportationToPage(pageId, transportationToMove, targetPageId);
        
        toast({
          title: "Traslado movido",
          description: `O traslado foi movido para a página selecionada.`,
        });
      } 
      // Secondary approach: use the imported global function
      else if (typeof globalMoveTransportationToPageFunc === 'function') {
        console.log("[TransportationManager] Calling global function with:", {
          sourcePageId: pageId,
          transportationId: transportationToMove,
          targetPageId
        });
        
        globalMoveTransportationToPageFunc(pageId, transportationToMove, targetPageId);
        
        toast({
          title: "Traslado movido",
          description: `O traslado foi movido para a página selecionada usando a função global.`,
        });
      } 
      else {
        console.log("[TransportationManager] No movement function available:", {
          propFunction: typeof moveTransportationToPage,
          globalFunction: typeof globalMoveTransportationToPageFunc
        });
        
        toast({
          title: "Funcionalidade não disponível",
          description: "A função de mover não está disponível.",
        });
      }
    } catch (error) {
      console.error("[TransportationManager] Error moving transportation:", error);
      toast({
        title: "Erro ao mover traslado",
        description: "Ocorreu um erro ao tentar mover o traslado: " + (error instanceof Error ? error.message : String(error)),
        variant: "destructive"
      });
    }
    
    // Always clean up state
    setTransportationToMove(null);
  };

  return (
    <div className="space-y-4">
      {!hideTitle && (
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium">Traslados</h3>
          {!hideAddButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
              onClick={addTransportation}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Traslado
            </Button>
          )}
        </div>
      )}

      {hideTitle && !hideAddButton && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
            onClick={addTransportation}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Traslado
          </Button>
        </div>
      )}

      {(!transportations || transportations.length === 0) ? (
        <div className="p-6 border border-dashed rounded-md text-center">
          <Car className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Você ainda não adicionou nenhum traslado a esta cotação.</p>
          {!hideAddButton && (
            <Button 
              variant="outline"
              onClick={addTransportation}
              className="bg-white hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Traslado
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {transportations.map((transportation: TransportationInfo, idx: number) => (
            <TransportationForm
              key={transportation.id}
              transportation={transportation}
              index={idx}
              pageId={pageId}
              updateTransportation={(pageId, transportationId, field, value) => {
                // For single field updates
                updateTransportation(transportationId, field, value);
              }}
              batchUpdateTransportation={batchUpdateTransportation}
              deleteTransportation={(pageId, transportationId) => {
                removeTransportation(transportationId);
              }}
              duplicateTransportation={duplicateTransportation}
              moveTransportationToPage={handleInitiateTransportationMove}
              isCollapsed={collapsedItems[transportation.id] !== false}
              onToggleCollapse={() => toggleTransportationCollapse(transportation.id)}
            />
          ))}
        </div>
      )}

      {/* Page Selection Modal */}
      <PageSelectionModal
        isOpen={isPageSelectionOpen}
        onClose={() => {
          setIsPageSelectionOpen(false);
          setTransportationToMove(null);
        }}
        pages={allPages}
        onSelectPage={handleMoveToPage}
        currentPageId={pageId}
        title="Selecionar página para mover o traslado"
      />
    </div>
  );
} 